import { useLocation, useModel } from '@umijs/max';
import { useEffect, useSyncExternalStore } from 'react';
import {
  getActivityProcessEntrySnapshot,
  loadActivityProcessEntry,
  subscribeActivityProcessEntry,
} from '@/utils/activityProcessEntry';
import { getAuthSession, getAuthSessionGeneration } from '@/utils/authSession';

// Menu hints share the page resource; actual resource permissions remain server-scoped.
export default function ActivityProcessEntrySync() {
  const { initialState, setInitialState } = useModel('@@initialState');
  const location = useLocation();
  const ownerId = initialState?.currentUser?.id;
  const entry = useSyncExternalStore(
    subscribeActivityProcessEntry,
    getActivityProcessEntrySnapshot,
    getActivityProcessEntrySnapshot,
  );

  useEffect(() => {
    setInitialState((previous) => {
      if (!previous || previous.currentUser?.id !== ownerId) return previous;
      const sameSession =
        ownerId &&
        entry.ownerId === ownerId &&
        entry.generation === getAuthSessionGeneration();
      if (!sameSession) {
        return previous.activityProcessEntry
          ? { ...previous, activityProcessEntry: undefined }
          : previous;
      }
      // A network error is not proof of lost access. Keep only the navigation hint;
      // the workflow page suspends actions until entry is confirmed.
      if (entry.status !== 'ready' && !entry.denied) return previous;
      const visible = entry.status === 'ready' && entry.data?.visible === true;
      if (
        previous.activityProcessEntry?.ownerId === ownerId &&
        previous.activityProcessEntry.visible === visible
      ) return previous;
      return { ...previous, activityProcessEntry: { ownerId, visible } };
    });
  }, [ownerId, entry, setInitialState]);

  useEffect(() => {
    const ensure = () => {
      if (ownerId && getAuthSession()?.currentUser?.id === ownerId) loadActivityProcessEntry().catch(() => undefined);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') ensure();
    };
    ensure();
    window.addEventListener('focus', ensure);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', ensure);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ownerId, location.pathname, entry.generation]);
  return null;
}
